const TAVILY_SEARCH_URL = "https://api.tavily.com/search";

export interface TavilySearchHit {
	title: string;
	url: string;
	content: string;
	/** Full page extract when includeRawContent is set. */
	rawContent?: string;
	score?: number;
}

export interface TavilyImageHit {
	url: string;
	description?: string;
}

export interface TavilySearchResponse {
	query: string;
	results: TavilySearchHit[];
	images: TavilyImageHit[];
	answer?: string;
}

export interface TavilySearchInput {
	query: string;
	topic?: "general" | "news" | "finance";
	searchDepth?: "basic" | "advanced" | "fast" | "ultra-fast";
	maxResults?: number;
	timeRange?: "day" | "week" | "month" | "year";
	includeImages?: boolean;
	includeImageDescriptions?: boolean;
	includeDomains?: string[];
	includeAnswer?: boolean | "basic" | "advanced";
	/** Request Tavily's extracted page text (`raw_content`). */
	includeRawContent?: boolean | "markdown" | "text";
}

function getTavilyApiKey(): string {
	return (process.env.TAVILY_API_KEY ?? "").trim();
}

export function hasTavilyApiKey(): boolean {
	return Boolean(getTavilyApiKey());
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function normalizeHits(raw: unknown): TavilySearchHit[] {
	if (!Array.isArray(raw)) return [];
	const hits: TavilySearchHit[] = [];
	for (const item of raw) {
		const row = asRecord(item);
		const url = typeof row?.url === "string" ? row.url.trim() : "";
		const title = typeof row?.title === "string" ? row.title.trim() : "";
		const content = typeof row?.content === "string" ? row.content.trim() : "";
		const rawContent =
			typeof row?.raw_content === "string" ? row.raw_content.trim() : "";
		if (!url || !title) continue;
		hits.push({
			title,
			url,
			content,
			rawContent: rawContent || undefined,
			score: typeof row?.score === "number" ? row.score : undefined,
		});
	}
	return hits;
}

function normalizeImages(raw: unknown): TavilyImageHit[] {
	if (!Array.isArray(raw)) return [];
	const images: TavilyImageHit[] = [];
	for (const item of raw) {
		if (typeof item === "string" && item.startsWith("http")) {
			images.push({ url: item.trim() });
			continue;
		}
		const row = asRecord(item);
		const url = typeof row?.url === "string" ? row.url.trim() : "";
		if (!url.startsWith("http")) continue;
		images.push({
			url,
			description:
				typeof row?.description === "string"
					? row.description.trim()
					: undefined,
		});
	}
	return images;
}

export async function tavilySearch(
	input: TavilySearchInput,
): Promise<TavilySearchResponse> {
	const query = input.query.trim();
	const empty: TavilySearchResponse = { query, results: [], images: [] };
	if (!query || !hasTavilyApiKey()) return empty;

	const body: Record<string, unknown> = {
		query: query.slice(0, 1500),
		search_depth: input.searchDepth ?? "advanced",
		max_results: Math.min(20, Math.max(1, input.maxResults ?? 8)),
		topic: input.topic ?? "general",
		include_images: Boolean(input.includeImages),
		include_image_descriptions: Boolean(input.includeImageDescriptions),
		include_answer: input.includeAnswer ?? false,
	};
	if (input.includeRawContent != null)
		body.include_raw_content = input.includeRawContent;
	if (input.timeRange) body.time_range = input.timeRange;
	if (input.includeDomains?.length)
		body.include_domains = input.includeDomains;

	const response = await fetch(TAVILY_SEARCH_URL, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${getTavilyApiKey()}`,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		const detail = await response.text().catch(() => "");
		throw new Error(
			`Tavily search failed (${response.status})${detail ? `: ${detail.slice(0, 240)}` : ""}`,
		);
	}

	const payload = asRecord(await response.json());
	return {
		query,
		results: normalizeHits(payload?.results),
		images: normalizeImages(payload?.images),
		answer:
			typeof payload?.answer === "string" && payload.answer.trim()
				? payload.answer.trim()
				: undefined,
	};
}
